import { useMemo, useState } from "react";
import {
  useClassificationRules,
  useCollectionJobs,
  useCancelCollectionJob,
} from "../../../queries";
import type { ClassificationRule } from "@/services/api/classification-rules";
import type { CollectionJob } from "@/services/api/collection-jobs";
import { CheckCircle2, List, TrendingUp, Tags, Download } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { CollectionJobsTable } from "./CollectionJobsTable";
import { CollectionJobCancelDialog } from "./CollectionJobCancelDialog";
import { ClassificationOnlyDialog } from "./ClassificationOnlyDialog";
import { ImportProfilesDialog } from "./ImportProfilesDialog";
import { CoreSignalGuideButton } from "../guide/CoreSignalGuideButton";
import { sortCollectionJobs } from "./collectionJobsUtils";
import { useDashboardSearchParams } from "./useDashboardSearchParams";
import { useUrlSortingSync } from "@/shared/hooks/useUrlSortingSync";

export function CoreSignalDashboardPanel() {
  const { data: rules, isLoading } = useClassificationRules();
  const { data: jobs } = useCollectionJobs();
  const cancelJobMutation = useCancelCollectionJob();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState<CollectionJob | null>(null);

  const { sorting: urlSorting, updateSearchParams } =
    useDashboardSearchParams();

  const { localSorting, setLocalSorting } = useUrlSortingSync(
    urlSorting,
    updateSearchParams
  );

  const sortColumn = localSorting.sortColumn;
  const sortDirection = localSorting.sortDirection;

  const effectiveSortColumn = sortColumn || "created";
  const effectiveSortDirection = sortColumn ? sortDirection : "desc";

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setLocalSorting({ sortDirection: "desc" });
      } else {
        setLocalSorting({ sortColumn: undefined, sortDirection: "asc" });
      }
    } else {
      setLocalSorting({ sortColumn: column, sortDirection: "asc" });
    }
  };

  const sortedJobs = useMemo(() => {
    if (!jobs) return [];
    return sortCollectionJobs(
      jobs,
      effectiveSortColumn,
      effectiveSortDirection
    );
  }, [jobs, effectiveSortColumn, effectiveSortDirection]);

  const stats = useMemo(() => {
    if (!rules) {
      return {
        total: 0,
        active: 0,
        categories: 0,
        totalKeywords: 0,
      };
    }

    const active = rules.filter((r: ClassificationRule) => r.isActive).length;
    const uniqueCategories = new Set(
      rules.map((r: ClassificationRule) => r.category)
    ).size;

    const totalKeywords = rules.reduce(
      (sum: number, r: ClassificationRule) => sum + (r.keywords?.length || 0),
      0
    );

    return {
      total: rules.length,
      active,
      categories: uniqueCategories,
      totalKeywords,
    };
  }, [rules]);

  const cards = useMemo(
    () => [
      {
        id: "total-rules",
        title: "Total Rules",
        description: `${stats.total} classification rules configured`,
        icon: List,
        value: stats.total,
      },
      {
        id: "active-rules",
        title: "Active Rules",
        description: `${stats.active} rules currently active`,
        icon: CheckCircle2,
        value: stats.active,
        color: "text-emerald-600",
      },
      {
        id: "categories",
        title: "Categories",
        description: `${stats.categories} unique categories`,
        icon: TrendingUp,
        value: stats.categories,
      },
      {
        id: "keywords",
        title: "Total Keywords",
        description: `${stats.totalKeywords} keywords across all rules`,
        icon: List,
        value: stats.totalKeywords,
      },
    ],
    [stats]
  );

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between">
        <p className="mt-1 text-sm text-ink-500">
          Overview of classification rules system status and metrics
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => setImportDialogOpen(true)}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Import Profiles
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setClassifyDialogOpen(true)}
          >
            <Tags className="h-4 w-4 mr-1.5" />
            Run Classification
          </Button>
          <CoreSignalGuideButton />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-xl border border-grid-200 bg-white p-4 shadow-sm shadow-ink-900/5 animate-pulse"
              >
                <div className="h-5 w-24 bg-grid-200 rounded mb-2" />
                <div className="h-3 w-32 bg-grid-100 rounded" />
              </div>
            ))
          : cards.map((card) => {
              const Icon = card.icon;
              return (
                <article
                  key={card.id}
                  className="rounded-xl border border-grid-200 bg-white p-4 shadow-sm shadow-ink-900/5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon
                          className={`h-4 w-4 shrink-0 ${card.color || "text-ink-600"}`}
                        />
                        <h3 className="text-sm font-semibold text-ink-900 truncate">
                          {card.title}
                        </h3>
                      </div>
                      <p className="text-xs text-ink-500 line-clamp-2">
                        {card.description}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-ink-900 ml-2 shrink-0">
                      {card.value}
                    </div>
                  </div>
                </article>
              );
            })}
      </div>

      <div className="rounded-xl border border-grid-200 bg-white shadow-sm overflow-hidden">
        <CollectionJobsTable
          jobs={sortedJobs}
          onCancel={(job) => {
            setJobToCancel(job);
            setCancelDialogOpen(true);
          }}
          isCancelling={cancelJobMutation.isPending}
          sortColumn={effectiveSortColumn}
          sortDirection={effectiveSortDirection}
          onSort={handleSort}
        />
      </div>

      <CollectionJobCancelDialog
        open={cancelDialogOpen}
        job={jobToCancel}
        isLoading={cancelJobMutation.isPending}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) {
            setJobToCancel(null);
          }
        }}
        onConfirm={() => {
          if (jobToCancel) {
            cancelJobMutation.mutate(jobToCancel.id, {
              onSuccess: () => {
                setCancelDialogOpen(false);
                setJobToCancel(null);
              },
            });
          }
        }}
      />

      <ClassificationOnlyDialog
        open={classifyDialogOpen}
        onOpenChange={setClassifyDialogOpen}
      />

      <ImportProfilesDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </div>
  );
}
